import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectService } from '../../services/api';
import { getMyProjects } from '../../api/myProjectsApi';
// WI-S4 §2 — signal columns in My Projects list.
import { getMyDeliverySignals } from '../../api/deliveryApi';
import * as live from '../../api/liveClient';
import renderPage from './ProjectListPage.render';
import ProjectPropertiesDrawer from './ProjectPropertiesDrawer';
import './ProjectListPage.css';

// NF-S2: `mode` toggles between the PM view ("manage", default) and the
// consultant view ("my"). "my" fetches the member-filtered list and routes
// row clicks to /my-projects/{id}; all edit affordances are hidden.
const ProjectListPage = ({ mode = 'manage' }) => {
  const isMyMode = mode === 'my';
  const navigate = useNavigate();

  // PL-S3: Normal | Gantt view, driven by the ?view= URL query param
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'gantt' ? 'gantt' : 'normal';
  const handleViewChange = (newView) => {
    const next = new URLSearchParams(searchParams);
    if (newView === 'normal') next.delete('view');
    else next.set('view', newView);
    setSearchParams(next, { replace: false });
  };

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // WI-S4 §2 — { projectId → {myOpen, forTakeover, late, myHours} }.
  const [signals, setSignals] = useState({});
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortCol, setSortCol] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [ctx, setCtx] = useState({ open: false, x: 0, y: 0, id: null });
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snack, setSnack] = useState('');
  const snackTimerRef = useRef(null);

  // PP-S1: create-project modal opens in-place (no dedicated route).
  const [createOpen, setCreateOpen] = useState(false);

  // PLX-S1: resizable grid columns — widths persisted to localStorage.
  const DEFAULT_LIST_COLUMNS = useMemo(() => ({
    name: 220, code: 90, client: 130, start: 90, end: 90,
    durationDays: 80, completionPercent: 120, status: 90,
  }), []);
  const MIN_LIST_COL_WIDTH = 30;
  const listColumnsKey = 'hrapp.projects.list.columns';

  const [colWidths, setColWidths] = useState(() => {
    try {
      const stored = localStorage.getItem(listColumnsKey);
      if (stored) return { ...DEFAULT_LIST_COLUMNS, ...JSON.parse(stored) };
    } catch {}
    return DEFAULT_LIST_COLUMNS;
  });

  useEffect(() => {
    try { localStorage.setItem(listColumnsKey, JSON.stringify(colWidths)); } catch {}
  }, [colWidths]);

  const startColResize = useCallback((e, colKey) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colKey];
    const onMove = (ev) => {
      const next = Math.max(MIN_LIST_COL_WIDTH, startW + (ev.clientX - startX));
      setColWidths(w => ({ ...w, [colKey]: next }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths]);

  const showSnack = useCallback((msg) => {
    setSnack(msg);
    if (snackTimerRef.current) clearTimeout(snackTimerRef.current);
    snackTimerRef.current = setTimeout(() => setSnack(''), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isMyMode) {
        // WI-S4 §2 — fetch the list first (required for the page);
        // signals go on their own path so a failure there just
        // leaves the columns blank instead of blocking the list.
        const data = await getMyProjects();
        setProjects(Array.isArray(data) ? data : []);
        try {
          const sigs = await getMyDeliverySignals();
          const map = {};
          for (const s of (Array.isArray(sigs) ? sigs : [])) {
            if (s && s.projectId != null) map[s.projectId] = s;
          }
          setSignals(map);
        } catch { setSignals({}); }
      } else {
        const data = await projectService.getAll({ metrics: 'portfolio' });
        setProjects(Array.isArray(data) ? data : []);
        setSignals({});
      }
    } catch (e) {
      setError(e?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [isMyMode]);

  useEffect(() => { load(); }, [load]);

  // UDRAW-S5.1 §2.2.2 — live signals on the "My Projects" list. When
  // the list lands we JoinProject for every row; any deliveryChanged
  // on any of them triggers a debounced (~500 ms) refetch of just the
  // signals aggregates (not the whole list — the list itself only
  // changes on membership updates, which are rare). Leave every
  // joined project on unmount. Silent if the hub is down.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!isMyMode) return undefined;
    const ids = projects.map((p) => p?.id).filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      await live.connect();
      if (cancelled) return;
      for (const id of ids) await live.joinProject(id);
    })();
    const refetchSignals = async () => {
      try {
        const sigs = await getMyDeliverySignals();
        const map = {};
        for (const s of (Array.isArray(sigs) ? sigs : [])) {
          if (s && s.projectId != null) map[s.projectId] = s;
        }
        setSignals(map);
      } catch { /* leave prior signals in place */ }
    };
    const off = live.on('deliveryChanged', (payload) => {
      if (!payload) return;
      // Accept reconnect (projectId=null) and any project this list
      // is joined to. Signals for other projects can't move our rows.
      const pid = payload.projectId;
      if (pid != null && !ids.includes(pid)) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        refetchSignals();
      }, 500);
    });
    return () => {
      cancelled = true;
      if (typeof off === 'function') off();
      for (const id of ids) live.leaveProject(id);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyMode, projects.map((p) => p?.id).join(',')]);

  // Close context menu on outside click
  useEffect(() => {
    if (!ctx.open) return undefined;
    const handler = () => setCtx(c => ({ ...c, open: false }));
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [ctx.open]);

  // Normalize row for display/sort
  const rows = useMemo(() => projects.map(p => {
    const sell = Number(p.totalSellBudget ?? p.TotalSellBudget ?? 0);
    const cost = Number(p.totalCostBudget ?? p.TotalCostBudget ?? 0);
    const margin = sell > 0 ? Math.round((sell - cost) / sell * 100) : 0;
    const startDate = p.startDate ?? p.StartDate ?? null;
    const endDate = p.endDate ?? p.EndDate ?? null;
    const id = p.id ?? p.Id;
    const sig = signals[id] || {};
    return {
      id,
      code: p.code ?? p.Code ?? '',
      name: p.name ?? p.Name ?? '',
      client: p.clientName ?? p.ClientName ?? '',
      status: p.status ?? p.Status ?? 'Draft',
      currency: p.currencyCode ?? p.CurrencyCode ?? 'EUR',
      roles: Number(p.roleCount ?? p.RoleCount ?? 0),
      tasks: Number(p.taskCount ?? p.TaskCount ?? 0),
      duration: Number(p.totalDurationDays ?? p.TotalDurationDays ?? 0),
      sell, cost, margin,
      // PL-S3 portfolio metrics
      startDate,
      endDate,
      // sort keys: ISO strings sort chronologically; empty string sorts first
      start: startDate || '',
      end: endDate || '',
      durationDays: Number(p.durationDays ?? p.DurationDays ?? 0),
      completionPercent: Number(p.completionPercent ?? p.CompletionPercent ?? 0),
      totalPlannedHours: Number(p.totalPlannedHours ?? p.TotalPlannedHours ?? 0),
      equivalentFteAt75: Number(p.equivalentFteAt75 ?? p.EquivalentFteAt75 ?? 0),
      // VW-S5 hotfix: preserve per-project phase breakdown so PortfolioGanttView's
      // Phase mode can render its phase rows.
      phases: p.phases ?? p.Phases ?? [],
      // WI-S4 §2 — sortable signal keys.
      myOpen: Number(sig.myOpen || 0),
      forTakeover: Number(sig.forTakeover || 0),
      late: Number(sig.late || 0),
      myHours: Number(sig.myHours || 0),
    };
  }), [projects, signals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (activeFilter !== 'All' && r.status !== activeFilter) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q)
        || r.code.toLowerCase().includes(q)
        || (r.client || '').toLowerCase().includes(q);
    });
  }, [rows, search, activeFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortCol, sortAsc]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(true); }
  };
  const sortArrow = (col) => sortCol !== col ? '' : (sortAsc ? '\u25B2' : '\u25BC');

  const handleRowClick = (id) => setSelectedId(id);
  const handleRowDouble = (id) => navigate(isMyMode ? `/my-projects/${id}` : `/projects/${id}`);

  const openCtx = (e, id) => {
    if (isMyMode) return;             // no context menu in the consultant view
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(id);
    setCtx({ open: true, x: e.clientX, y: e.clientY, id });
  };

  const askDelete = (id) => {
    const row = rows.find(r => r.id === id);
    if (row) setDelTarget(row);
  };
  const closeDel = () => { if (!deleting) setDelTarget(null); };
  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await projectService.delete(delTarget.id);
      const code = delTarget.code;
      setDelTarget(null);
      showSnack(`Project ${code} deleted.`);
      await load();
    } catch (e) {
      showSnack('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {renderPage({
        loading, error, rows, sorted, search, setSearch, activeFilter, setActiveFilter,
        sortCol, sortAsc, toggleSort, sortArrow, selectedId, handleRowClick, handleRowDouble,
        openCtx, load, ctx, setCtx, askDelete, navigate, showSnack,
        delTarget, closeDel, doDelete, deleting, snack,
        view, handleViewChange, colWidths, startColResize,
        // NF-S2 flags consumed by ProjectListPage.render.js to hide
        // edit-only chrome (create button, context-menu trigger, delete modal).
        isMyMode,
        // WI-S4 §2 — { projectId → { myOpen, forTakeover, late, myHours } }.
        signals,
        linkBase: isMyMode ? '/my-projects' : '/projects',
        onCreateClick: isMyMode ? () => {} : () => setCreateOpen(true),
      })}
      {!isMyMode && (
        <ProjectPropertiesDrawer
          mode="create"
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={(newProject) => {
            setCreateOpen(false);
            const newId = newProject?.id ?? newProject?.Id;
            if (newId) navigate(`/projects/${newId}`);
          }}
        />
      )}
    </>
  );
};

export default ProjectListPage;
